import authController from '../controllers/auth.controller.js'

import { useRouter } from 'vue-router'

import { useAuthStore } from '../stores/authStore.js'
import { useScopeStore } from '../stores/scopeStore.js'
import { useTicketGroupStore } from '../stores/tickets/ticketGroupStore.js'
import { useTicketSettingsStore } from '../stores/tickets/ticketSettingsStore.js'
import { useTicketStore } from '../stores/tickets/ticketStore.js'

import authUtility from '../utils/auth.utility.js'

import { useNotifications } from './useNotifications.js'
import { useScope } from './useScope.js'

/** Shared by boot, the router guard, and tab-focus sync — one in-flight restore. */
let promise = null

/**
 * Route snapshot for scope restore. Prefer the live browser URL — during the
 * initial navigation `router.currentRoute` is still START while beforeEach
 * awaits this restore, so admin deep links would otherwise look like `/`.
 *
 * @param {import('vue-router').Router} router
 * @returns {{ path: string, matched: Array<{ meta?: object }>, meta?: object }}
 */
function resolveSessionRoute(router) {
  const current = router.currentRoute.value
  const path =
    typeof window !== 'undefined' && window.location?.pathname
      ? window.location.pathname
      : (current?.path ?? '')
  if (current?.matched?.length && current.path === path) {
    return current
  }
  return { path, matched: [], meta: {} }
}

/**
 * Restore the session on boot or when guards / sync re-verify auth.
 * Accepts the router instance so navigation guards can call this outside
 * `setup()` without tripping Vue's inject warnings.
 *
 * @param {import('vue-router').Router} router
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<void>}
 */
export async function initUserSession(router, { force = false } = {}) {
  if (promise && !force) return promise

  const scope = useScope()
  const notifications = useNotifications()

  const authStore = useAuthStore()
  const scopeStore = useScopeStore()
  const ticketStore = useTicketStore()
  const ticketSettingsStore = useTicketSettingsStore()
  const ticketGroupStore = useTicketGroupStore()

  promise = (async () => {
    try {
      const user = await authController.get.current()
      if (!user) {
        authStore.reset()
        scope.reset()
        ticketStore.reset()
        ticketSettingsStore.reset()
        ticketGroupStore.reset()
        notifications.reset()
        authUtility.remove()
        return
      }
      authStore.hydrate.user(user)
      authUtility.set()
      await scope.rehydrateFromRoute(resolveSessionRoute(router))
      // scopeNeutral early-exit (or empty path) can leave kind null on first boot.
      if (!scopeStore.scope.kind) {
        if (user.groupMode) await scope.setGroup()
        else scope.setAccount()
      }
    } catch {
      authStore.reset()
      scope.reset()
      ticketStore.reset()
      ticketSettingsStore.reset()
      ticketGroupStore.reset()
      notifications.reset()
      authUtility.remove()
    }
  })()

  return promise
}

export function useUser() {
  const router = useRouter()
  const scope = useScope()
  const notifications = useNotifications()

  const authStore = useAuthStore()
  const ticketStore = useTicketStore()
  const ticketSettingsStore = useTicketSettingsStore()
  const ticketGroupStore = useTicketGroupStore()

  /**
   * Clear session, scope, and domain stores, then the local auth hint.
   *
   * @returns {void}
   */
  function reset() {
    authStore.reset()
    scope.reset()
    ticketStore.reset()
    ticketSettingsStore.reset()
    ticketGroupStore.reset()
    notifications.reset()
    authUtility.remove()
  }

  /**
   * Restore the session on boot or when guards / sync re-verify auth.
   * Dedupes concurrent callers via `initPromise`; pass `force` to bypass
   * the cache after tab focus or a failed earlier attempt.
   *
   * Always calls the API — session cookies are HttpOnly and may not be
   * readable from JS (cross-port v1 handoff shares cookies, not localStorage).
   *
   * @param {{ force?: boolean }} [opts]
   * @returns {Promise<void>}
   */
  async function init({ force = false } = {}) {
    return initUserSession(router, { force })
  }

  /**
   * Authenticate and hydrate the session and scope. Re-throws on failure after
   * reset.
   *
   * @param {{ email: string, password: string }} credentials
   * @returns {Promise<void>}
   */
  async function login(credentials) {
    try {
      const user = await authController.login(credentials)
      authStore.hydrate.user(user)
      authUtility.set()
      if (user.groupMode) await scope.setGroup()
      else scope.setAccount()
    } catch (err) {
      reset()
      throw err
    }
  }

  /**
   * Invalidate the session and clear local state.
   *
   * @returns {Promise<void>}
   */
  async function logout() {
    await authController.logout()
    reset()
  }

  /**
   * Switch account, flush ticket stores, and force account layout.
   *
   * @param {string} accountId
   * @returns {Promise<boolean>}
   */
  async function switchAccount(accountId) {
    const user = await authController.update.account(accountId)
    if (!user) return false
    ticketStore.reset()
    ticketSettingsStore.reset()
    ticketGroupStore.reset()
    authStore.hydrate.user({ ...user, groupMode: false })
    scope.setAccount()
    return true
  }

  /**
   * Enter group mode and flush ticket stores. No-op when already in group mode.
   *
   * @returns {Promise<boolean>}
   */
  async function setGroupMode() {
    if (authStore.user?.groupMode) return true
    const res = await authController.update.groupmode()
    if (res != null) {
      ticketStore.reset()
      ticketSettingsStore.reset()
      ticketGroupStore.reset()
      const user = await authController.get.current()
      if (user) authStore.hydrate.user(user)
      else authStore.hydrate.user({ ...authStore.user, groupMode: res })
      if (authStore.user?.ticketGroups?.length) {
        ticketGroupStore.hydrate.groups({ items: authStore.user.ticketGroups, _meta: null })
      }
      await scope.setGroup()
    }
    return res != null
  }

  /**
   * Leave group mode and flush ticket stores. No-op when already out of group
   * mode. Pass `applyAccountScope: false` when the caller will set another
   * shell next (e.g. admin) so we do not briefly land in account scope.
   *
   * @param {{ applyAccountScope?: boolean }} [opts]
   * @returns {Promise<boolean>}
   */
  async function setAccountMode({ applyAccountScope = true } = {}) {
    if (!authStore.user?.groupMode) return true
    const res = await authController.update.groupmode()
    if (res != null) {
      ticketStore.reset()
      ticketSettingsStore.reset()
      ticketGroupStore.reset()
      authStore.hydrate.user({ ...authStore.user, groupMode: res })
      if (applyAccountScope) scope.setAccount()
    }
    return res != null
  }

  /**
   * Re-fetch the current user and re-hydrate session and scope, or reset when
   * logged out.
   *
   * @returns {Promise<void>}
   */
  async function refresh() {
    return init({ force: true })
  }

  return {
    init,
    login,
    logout,
    switchAccount,
    setGroupMode,
    setAccountMode,
    refresh,
    reset,
  }
}
